// The tool pickers on the Control Room's automation cards, with readiness:
// each card lists every CONNECTED tool of its kind, offers the ones that are
// set up, greys out the ones that are not (saying what to finish), and says so
// when its saved pick cannot be used. Per card:
//   Auto-create & update tasks: settings.taskWriteBackend (taskToolsRow)
//   Auto-create meetings:       settings.calendarWriteBackend (calendarToolsRow)
//   Daily status pings, Auto-escalations, Executive report: their own
//     automationToolPrefs key (dailyStatusChat, escalationsChat,
//     weeklyReportChat), falling back to settings.chatWriteBackend (chatToolsRow)
//
// Each row is built here as plain data so the rules can be tested without
// rendering the Control Room:
//   { mode, options, value, effective, tool, text, notice, placeholder }
// mode "select": a dropdown of `options` showing `value`;
// mode "single": `text` ("Uses Asana") with `tool`'s logo;
// mode "none":   `text` alone (a connect hint, or what to finish).
// `effective` is the tool BossDrop really uses, by the server's rules, and
// `notice` the one sentence shown when the saved pick is not that tool.
import {
  TASK_TOOL_LABELS,
  TASK_TOOL_ORDER,
  pickTaskBackend,
} from "./taskTools";
import { taskToolFor } from "./automationChips";

// Where tools are connected and set up: the Connectors card in Control Room.
const CONNECTORS = "Connectors";
// Calendars are connected per person, on their card in Team directory.
const calendarPlace = (whose = "the owner's") => `on ${whose} card in Team directory`;

export const CHAT_TOOL_ORDER = ["teams", "slack"];
export const CHAT_TOOL_LABELS = {
  teams: "Microsoft Teams",
  slack: "Slack",
};

export const CALENDAR_TOOL_ORDER = ["google", "microsoft"];
export const CALENDAR_TOOL_LABELS = {
  google: "Google Calendar",
  microsoft: "Microsoft (Outlook + Teams)",
};
// ToolIcon keys for the logo beside "Uses ...".
const CALENDAR_TOOL_ICONS = { google: "google", microsoft: "outlook" };
// Today's server behavior with no choice saved (createMeeting in
// backend/server/autoScheduleMeetings/index.js).
export const CALENDAR_DEFAULT_LABEL = "Default (Google first, then Microsoft)";

function key(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

/**
 * settings.calendarWriteBackend as stored: "google" | "microsoft", or null for
 * the default. Anything else reads as the default.
 */
export function calendarWriteBackendShape(value) {
  const k = key(value);
  return CALENDAR_TOOL_ORDER.includes(k) ? k : null;
}

/**
 * The chat tool BossDrop writes to: the one asked for when it is connected,
 * else Teams, else Slack (pickChatBackend in backend/server/automationWorker.js).
 */
export function pickChatBackend(want, connected = {}) {
  const w = key(want);
  if (CHAT_TOOL_ORDER.includes(w) && connected[w]) return w;
  return CHAT_TOOL_ORDER.find((tool) => !!connected[tool]) || null;
}

/**
 * The calendar meetings are booked on: the one asked for when it is connected,
 * else Google, else Microsoft. `connected` is { google, microsoft }.
 */
export function pickCalendarBackend(want, connected = {}) {
  const w = calendarWriteBackendShape(want);
  if (w && connected[w]) return w;
  return CALENDAR_TOOL_ORDER.find((tool) => !!connected[tool]) || null;
}

/**
 * automationToolPrefs without the old per-automation New tasks override
 * (autoTasksBackend), so the task card's choice (settings.taskWriteBackend,
 * which the planner prompt and the executor read) is the one that applies.
 * Returns `prefs` itself when there is nothing to drop.
 */
export function withoutTaskOverride(prefs) {
  if (
    !prefs ||
    typeof prefs !== "object" ||
    !Object.prototype.hasOwnProperty.call(prefs, "autoTasksBackend")
  ) {
    return prefs;
  }
  const { autoTasksBackend: _dropped, ...rest } = prefs;
  return rest;
}

function using(label, nothing) {
  return label ? `BossDrop is using ${label}` : nothing;
}

/**
 * The task card's row. `states` is taskToolStates() (lib/taskTools.js), `saved` is
 * settings.taskWriteBackend and `override` automationToolPrefs.autoTasksBackend,
 * which the worker still honours ahead of the workspace choice when that tool
 * is ready (resolveTaskBackend), so the row shows where new tasks REALLY go.
 */
export function taskToolsRow({ states = [], saved = "", override = "" } = {}) {
  const ready = {};
  for (const s of states) ready[s.key] = !!s.ready;
  const known = (value) => (TASK_TOOL_ORDER.includes(key(value)) ? key(value) : "");
  const effective = taskToolFor(override, ready, pickTaskBackend(saved, ready));
  const connected = states.filter((s) => s.connected);
  const label = (tool) => TASK_TOOL_LABELS[tool] || "";

  // What the workspace asked for: the old override when one is set, else the
  // workspace choice.
  const wantOverride = known(override);
  const want = wantOverride || known(saved);
  let notice = null;
  if (want && want !== effective && connected.length) {
    const state = states.find((s) => s.key === want) || {};
    const fallback = using(label(effective), "BossDrop can't create tasks yet");
    // Only a pick that is connected but not fully set up is worth a word. A
    // saved pick whose tool is not connected at all (a stale choice, e.g. a
    // tool disconnected since) is ignored without comment: the founder, Oct
    // 20: a warning about a tool he does not use "should NOT be here".
    if (state.connected) {
      notice = `${label(want)} isn't fully set up, so ${fallback}. Finish ${label(
        want,
      )} in ${CONNECTORS}.`;
    }
  }

  const options = connected.map((s) => ({
    value: s.key,
    label: s.ready ? s.label : `${s.label}: ${s.step}`,
    disabled: !s.ready,
    reason: s.ready ? "" : `Finish ${s.label} in ${CONNECTORS}`,
  }));

  if (!connected.length) {
    return {
      mode: "none",
      options: [],
      value: "",
      effective,
      tool: null,
      text: `Connect a task tool in ${CONNECTORS}.`,
      notice: null,
    };
  }
  if (connected.length === 1) {
    const only = connected[0];
    if (only.ready) {
      return {
        mode: "single",
        options,
        value: only.key,
        effective,
        tool: only.key,
        text: `Uses ${only.label}`,
        notice: want === only.key ? null : notice,
      };
    }
    return {
      mode: "none",
      options,
      value: "",
      effective,
      tool: null,
      text: `${only.label} isn't fully set up yet. In ${CONNECTORS}, ${only.step}.`,
      notice: want === only.key ? null : notice,
    };
  }
  return {
    mode: "select",
    options,
    value: effective || "",
    effective,
    tool: effective,
    text: "",
    notice,
    placeholder: "No task tool is ready yet",
  };
}

/**
 * A chat card's row (Daily status pings, Auto-escalations, Executive report).
 * `connected` is { teams, slack }; `own` is the card's automationToolPrefs key
 * (dailyStatusChat, escalationsChat, weeklyReportChat) and `workspace`
 * settings.chatWriteBackend, the fallback when the card has no pick of its
 * own. The server honours `own` when that tool is connected, else the
 * workspace's tool, else whichever is connected (resolveChatBackend in the
 * worker, resolveChatBackendForPurpose in executePlan).
 */
export function chatToolsRow({ connected = {}, own = "", workspace = "" } = {}) {
  const known = (value) => (CHAT_TOOL_ORDER.includes(key(value)) ? key(value) : "");
  const ownPick = known(own);
  const workspacePick = known(workspace);
  const fallback = pickChatBackend(workspacePick, connected);
  const effective = ownPick && connected[ownPick] ? ownPick : fallback;
  const tools = CHAT_TOOL_ORDER.filter((tool) => !!connected[tool]);
  const label = (tool) => CHAT_TOOL_LABELS[tool] || "";
  // A pick whose tool is not connected is ignored without comment (see
  // taskToolsRow); the row just shows the tool in use.
  const notice = null;
  const options = tools.map((tool) => ({ value: tool, label: label(tool) }));

  if (!tools.length) {
    return {
      mode: "none",
      options,
      value: "",
      effective,
      tool: null,
      text: `Connect Slack or Microsoft Teams in ${CONNECTORS}.`,
      notice: null,
    };
  }
  if (tools.length === 1) {
    return {
      mode: "single",
      options,
      value: tools[0],
      effective,
      tool: tools[0],
      text: `Uses ${label(tools[0])}`,
      notice,
    };
  }
  return {
    mode: "select",
    options,
    // Both are connected here, so this is the card's own pick when it has
    // one, else the tool the workspace fallback lands on.
    value: effective || "",
    effective,
    tool: effective,
    text: "",
    notice,
  };
}

/**
 * The meetings card's provider row. `calendars` is the meeting calendar
 * person's connections, { status, google, outlook } (lib/meetingCalendar.js
 * meetingPersonCalendars, or useCalendarConnections() for the owner); `whose`
 * names that person in a sentence ("Eric Holmes's", default "the owner's");
 * `saved` is settings.calendarWriteBackend.
 */
export function calendarToolsRow({
  calendars = {},
  saved = null,
  whose = "the owner's",
} = {}) {
  const want = calendarWriteBackendShape(saved);
  const base = {
    options: [],
    value: want || "",
    effective: null,
    tool: null,
    notice: null,
  };
  if (calendars?.status === "loading") {
    return { ...base, mode: "none", text: `Checking ${whose} calendars…` };
  }
  if (calendars?.status !== "ready") {
    return {
      ...base,
      mode: "none",
      text: `Couldn't check ${whose} calendars. Saved choice: ${
        want ? CALENDAR_TOOL_LABELS[want] : CALENDAR_DEFAULT_LABEL
      }.`,
    };
  }
  const connected = {
    google: !!calendars.google,
    microsoft: !!calendars.outlook,
  };
  const effective = pickCalendarBackend(want, connected);
  const tools = CALENDAR_TOOL_ORDER.filter((tool) => connected[tool]);
  // A pick whose calendar is not connected is ignored without comment.
  const notice = null;

  if (!tools.length) {
    return {
      ...base,
      mode: "none",
      text: `Connect Google Calendar or Outlook ${calendarPlace(whose)}.`,
    };
  }
  if (tools.length === 1) {
    return {
      ...base,
      mode: "single",
      options: [{ value: tools[0], label: CALENDAR_TOOL_LABELS[tools[0]] }],
      value: tools[0],
      effective,
      tool: CALENDAR_TOOL_ICONS[tools[0]],
      text: `Uses ${CALENDAR_TOOL_LABELS[tools[0]]}`,
      notice,
    };
  }
  return {
    ...base,
    mode: "select",
    options: [
      { value: "", label: CALENDAR_DEFAULT_LABEL },
      ...tools.map((tool) => ({ value: tool, label: CALENDAR_TOOL_LABELS[tool] })),
    ],
    value: want || "",
    effective,
    tool: CALENDAR_TOOL_ICONS[effective] || null,
    text: "",
    notice,
  };
}
